import { ValeurIndiceDto } from 'api/indice';
import { formatDay, toDayInput } from 'core/utils/dates';

/**
 * Période de validité d'une valeur d'indice.
 *
 * Le back ne stocke qu'une date de début : c'est la valeur suivante qui borne la
 * précédente. La borne de fin est donc calculée ici, à l'affichage — la dernière valeur
 * reste ouverte.
 */
export interface IndiceValuePeriod {
  value: ValeurIndiceDto;
  /** « 01/01/2026 » */
  startLabel: string;
  /** « 31/03/2026 », ou null pour la valeur courante qui n'a pas de fin. */
  endLabel: string | null;
  isCurrent: boolean;
}

/** Comparable de tri sur une date calendaire, sans jamais construire de Date. */
const dayKey = (value?: ValeurIndiceDto | null): string => toDayInput(value?.startDate) || '';

/** Veille du jour donné, en restant sur des dates calendaires. */
const previousDay = (isoDay: string): string => {
  const [year, month, day] = isoDay.split('-').map(Number);
  // Date UTC : purement arithmétique, aucun décalage de fuseau n'intervient.
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
};

const frenchDay = (isoDay: string): string => {
  const [year, month, day] = isoDay.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Ordonne les valeurs de la plus récente à la plus ancienne et calcule leur période.
 *
 * Une liste de dates isolées se lit mal : « du 01/01 au 31/03 » dit immédiatement quelle
 * valeur s'applique quand, et rend visible un trou ou un doublon de saisie.
 */
export const toValuePeriods = (values?: ValeurIndiceDto[] | null): IndiceValuePeriod[] => {
  const sorted = [...(values ?? [])].sort((a, b) => dayKey(b).localeCompare(dayKey(a)));

  return sorted.map((value, index) => {
    // Les valeurs sont triées du plus récent au plus ancien : la borne de fin d'une
    // valeur est la veille du début de celle qui la précède dans la liste.
    const next = index > 0 ? sorted[index - 1] : null;
    const nextStart = next ? dayKey(next) : '';

    return {
      value,
      startLabel: formatDay(value.startDate),
      endLabel: nextStart ? frenchDay(previousDay(nextStart)) : null,
      isCurrent: index === 0,
    };
  });
};

/** Formate une valeur d'indice avec ses trois décimales. */
export const formatIndiceValue = (value?: number | null): string =>
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(value);
